/* ===========================================================
   state.js — app-wide state (Preact signals)

   session  who is signed in, their Docs profile, Weather Protocol look
   mode     'casual' | 'dev'
   online   the browser's own idea of the network
   docs     the doc list (this device's copy, refreshed from the server)
   store    this account's on-device storage (IndexedDB)

   The last /api/me answer is cached, so the app opens instantly (and
   offline) as whoever was signed in; it's checked again in the
   background.
   =========================================================== */

import { signal, computed } from '@preact/signals';
import { api, HttpError } from './lib/api.js';
import { IDBStore } from './sync/store.js';
import { coloursFor, applyTheme } from './lib/theme.js';
import { newId } from '../shared/protocol.js';

export const session = signal({ status: 'loading' });
export const mode = computed(() => session.value.profile?.mode || 'casual');
export const online = signal(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
export const docs = signal([]);
export const store = signal(null);
/** Set by the doc page so the search menu can offer doc actions. */
export const docContext = signal(null);

const ME_KEY = 'dp:me';

export function deviceId() {
  try {
    let id = localStorage.getItem('dp:dev');
    if (!id) { id = newId(12); localStorage.setItem('dp:dev', id); }
    return id;
  } catch { return 'nostore'; }
}

function setSession(me, { cached = false } = {}) {
  if (!me?.user) {
    session.value = { status: 'signed-out' };
    store.value = null;
    applyTheme(coloursFor(null));
    return;
  }
  const status = me.access ? (me.profile?.mode && me.profile?.onboarded ? 'ready' : 'welcome') : 'gate';
  session.value = { status, cached, user: me.user, profile: me.profile, wp: me.wp, testing: me.testing, usage: me.usage, owner: !!me.owner };
  if (me.access && (!store.value || store.value.name !== `dp-${me.user.id}`)) store.value = new IDBStore(`dp-${me.user.id}`);
  applyTheme(coloursFor(me.profile, me.wp));
}

export async function loadSession() {
  let cached = null;
  try { cached = JSON.parse(localStorage.getItem(ME_KEY) || 'null'); } catch { /* none */ }
  if (cached?.user) setSession(cached, { cached: true });
  try {
    const me = await api('/api/me');
    try { localStorage.setItem(ME_KEY, JSON.stringify(me)); } catch { /* private mode */ }
    setSession(me);
  } catch (err) {
    if (!cached?.user) {
      session.value = err instanceof HttpError && err.offline
        ? { status: 'offline-first' }
        : { status: 'error', error: err };
    }
  }
}

/** Patch the profile here at once and on the server (best effort when offline). */
export async function updateProfile(patch) {
  const s = session.value;
  if (!s.profile) return;
  const profile = { ...s.profile, ...patch, prefs: { ...s.profile.prefs, ...(patch.prefs || {}) } };
  const status = s.status === 'welcome' && profile.mode && profile.onboarded ? 'ready' : s.status;
  session.value = { ...s, profile, status };
  applyTheme(coloursFor(profile, s.wp));
  cacheMe();
  const r = await api('/api/profile', { method: 'PATCH', body: patch });
  session.value = { ...session.value, profile: { ...r.profile, ...session.value.profile, prefs: { ...r.profile.prefs, ...session.value.profile.prefs } } };
  cacheMe();
  return r.profile;
}

function cacheMe() {
  const s = session.value;
  try {
    localStorage.setItem(ME_KEY, JSON.stringify({ user: s.user, access: true, profile: s.profile, wp: s.wp, testing: s.testing, usage: s.usage }));
  } catch { /* private mode */ }
}

export function forgetSession() {
  try { localStorage.removeItem(ME_KEY); } catch { /* fine */ }
}

export const pref = (k, fallback) => session.value.profile?.prefs?.[k] ?? fallback;
